import type { GroupChangeEvent } from './groupNavigation';
import { pickLocale, type Characteristic, type CharacteristicVisibility, type Component } from '../schema';
import type { Value } from '../schema/value';
import { resolveCustomFields, type CustomFieldEntry } from './customFields';
import type { ConfigentoStore } from '../state/store';
import type { CharacteristicSelection, SelectionsMap } from '../state/selections';
import type { EvaluatedValue } from '../eval/types';
import type { ExternalValues } from '../eval/evaluator';
import type { ValidationError } from '../validation/validate';
import { validateConfiguration } from '../validation/validate';
import { deriveConfiguration, type DerivedConfiguration } from '../state/derived';
import { calculatePrice } from '../pricing/calculate';
import { formatCurrency } from '../pricing/format';
import type { PriceBreakdown } from '../pricing/types';
import { translate } from '../i18n';
import type { Locale } from '../i18n/types';
import { snapshotCombinedImage } from '../ui/combinedImage/snapshot';
import { buildAssetPayload, type AssetPayload } from './assetPayload';
import { restoreFromAssetJson } from './restoreFromAssetJson';
import { buildSummaryText, buildSummary, type BuildSummaryOpts } from './summary';
import {
    storeAssetPayload,
    storeCombinedImage as uploadCombinedImage,
    ConfigentoCloudError,
    type StoreAssetOpts,
} from './cloudAsset';
import type { WebshopConfig } from './webshopConfig';

export interface FinalizeResult {
    /** True when no validation errors — the caller can proceed with submit. */
    ok: boolean;
    validationErrors: ReadonlyArray<ValidationError>;
    /** Alt_title-keyed asset JSON (defined when `ok === true`). */
    payload?: AssetPayload;
    /** PNG data URL of the composite product image (only when component opts in). */
    combinedImage?: string;
    /** Sum of price contributions for the current selections, in the configurator's
     *  base currency (host-shop tax conversion has already been applied if active). */
    priceTotal: number;
    /** Compressed visible-text summary, ≤ 1024 chars. */
    summary: string;
}

export interface PriceSnapshot {
    total: number;
    currency: string;
    priceIncludesTax: boolean;
}

/**
 * Read-only view onto everything a host needs to mirror the configurator
 * state somewhere else (a live summary widget, a comparison table, an
 * analytics event). The shape is intentionally close to what
 * `finalize()` would build, minus the async snapshotCombinedImage step
 * and minus the validation gate — getState always returns even when the
 * configuration is incomplete.
 */
export interface ValueInfo {
    id: number;
    /** The Value's internal value (`value` in the payload), null when none is set. */
    key: string | null;
    label: string;
    sku: string | null;
    customFields: Record<string, CustomFieldEntry>;
}

export interface CharacteristicInfo {
    id: number;
    /** Internal name — the key in `getAssetPayload()`; null when none is set. */
    key: string | null;
    groupId: number;
    groupKey: string | null;
    title: string;
    type: string;
    visibility: CharacteristicVisibility;
    /** The editor's "CSS-Klasse" field as entered, '' when empty. */
    cssClass: string;
    customFields: Record<string, CustomFieldEntry>;
    /** Every Value, rule-blacklisted ones included. Empty for free-entry types. */
    values: ValueInfo[];
}

export interface ConfiguratorSnapshot {
    /**
     * The customer's configuration as it currently stands. A Characteristic
     * a rule has removed carries no entry at all, and a blacklisted Value is
     * gone from the ones that do — an invisible Characteristic has no value,
     * the same rule the total and the cart payload follow. The store still
     * remembers the original pick internally, so it reappears the moment the
     * rule stops matching; hosts never see the intermediate state.
     */
    selections: SelectionsMap;
    /** Output of every calculated Characteristic (expression / combi / matrix /
     *  productattribute), keyed by `characteristic.id`. Useful when a host
     *  wants the live formula result for a custom widget. */
    evaluated: Readonly<Record<number, EvaluatedValue>>;
    /** Currently-hidden Characteristic ids per rule evaluation. */
    hiddenCharacteristics: ReadonlyArray<number>;
    /** Currently-blacklisted Value ids per rule evaluation. */
    blacklistedValues: ReadonlyArray<number>;
    price: PriceSnapshot;
    /**
     * What each Characteristic adds to `price.total`, keyed by internal name
     * like `getAssetPayload()`, on the same currency and tax basis. Only
     * Characteristics that take part in the price and have an internal name
     * appear; a rule-removed one has no entry.
     */
    characteristicPrices: Readonly<Record<string, number>>;
}

/**
 * Reference to a Characteristic. Either the numeric `id` (stable across
 * a Component's lifetime) or the admin-authored `alt_title` (e.g.
 * `"Lamelle_01"`). Both are unique within a Component; alt_title is
 * usually what theme/plugin authors carry around because it's the same
 * identifier they write in expression formulas and in the asset payload.
 *
 * Numeric ids are still accepted because the host might also be working
 * from `getState()` output (which uses ids as map keys).
 */
export type CharacteristicRef = number | string;

/**
 * Reference to a Value within a Characteristic. Either the numeric `id`
 * or the admin-authored `value` string (the same one matrix lookups
 * compare against — e.g. `"holz"`). Both lookups are O(n) over the
 * Characteristic's values; the n is small (rarely > 50) so we don't
 * bother indexing.
 */
export type ValueRef = number | string;

/**
 * Payload of `on('change', cb)`. Fired after any selection mutation —
 * single-pick set, multi-pick toggle, text change, quantity update,
 * or programmatic reset.
 *
 * `selections` carries the same rule-filtered map {@link InstanceApi.getState}
 * returns, so a host mirroring the configuration never sees a value for a
 * Characteristic the page has stopped offering.
 */
export interface ChangeEvent {
    selections: SelectionsMap;
}

/**
 * Payload of `on('price', cb)`. Fired AFTER a `change` when the total
 * price actually shifted — pure no-op changes (toggling then untoggling)
 * don't re-fire. Carries the same shape as {@link getPriceBreakdown}.
 */
export interface PriceEvent extends PriceSnapshot {
    /** Delta vs the previous fired price, in the same currency. Positive
     *  when adding something, negative when removing. First emission has
     *  delta=0 if the configurator boots with selections already set. */
    delta: number;
}

/**
 * Payload of `on('error', cb)`. Fired when validation errors get
 * surfaced — typically right after a failed `finalize()` call, but
 * also whenever the host code pushes errors via the internal store
 * action (e.g. wizard step-validation).
 */
export interface ErrorEvent {
    errors: ReadonlyArray<ValidationError>;
}

/**
 * Payload of `on('action', cb)`. Fired when an action button's request has
 * finished — success, an error status from the endpoint, or no answer at
 * all — but not when validation stopped the click (that is `error`).
 *
 * A host that shows its own result (texts from its endpoint, replacing the
 * form) calls `preventDefault()`: the button then shows neither its success
 * nor its error line and does not redirect. A PDF download still starts.
 *
 * Action `event` (save_action `event`) sends nothing anywhere: the event
 * carries the validated `payload` — including the captcha solution as
 * `altcha` — for the page to send to its own server. The button stays busy
 * until the listener's returned promise settles; it resolves to
 * `{ ok, message? }` (rejecting counts as `ok: false`). Without a listener
 * the button reports an error.
 */
export interface ActionEvent {
    /** HTTP status 2xx. Always true for `event` — the page decides via its return value. */
    ok: boolean;
    /** HTTP status of the response; 0 when there was none (network error, `event`). */
    status: number;
    /** The response: parsed JSON, else its text; null for a PDF, no response or `event`. */
    body: unknown;
    /** Internal name (alt_title) of the action button. */
    characteristic: string;
    /** Which kind of action ran. */
    action: 'custom_url' | 'pdf' | 'email' | 'event';
    /** `event` only: the configuration to submit, the captcha solution as `altcha`. */
    payload?: AssetPayload;
    /** Keep the button's own success/error line and redirect from running. */
    preventDefault(): void;
    readonly defaultPrevented: boolean;
}

/** What an `on('action')` listener for action `event` resolves to. */
export interface ActionEventResult {
    ok: boolean;
    /** Shown under the button: on error instead of the status, on success instead of the default line. */
    message?: string;
}

/**
 * Typed event names supported by {@link InstanceApi.on}. Extending this
 * union is a minor-bump (subscribers stay compatible) — renaming or
 * removing an event is a major-bump.
 */
export type InstanceEventMap = {
    change:   ChangeEvent;
    price:    PriceEvent;
    finalize: FinalizeResult;
    error:    ErrorEvent;
    /** The visible group / wizard step changed — see {@link GroupChangeEvent}. */
    group:    GroupChangeEvent;
    /** An action button's request finished — see {@link ActionEvent}. */
    action:   ActionEvent;
};

export type InstanceEventName = keyof InstanceEventMap;

/**
 * Per-instance API surface plugged onto each {@link ConfiguratorInstance}.
 * Plugin authors (Shopify, Magento, standalone embeds) get this once the
 * configurator has finished loading its Component — until then the
 * methods throw with a clear "not ready yet" hint. Use `ready` on the
 * top-level `Configento` to wait for ALL instances at once, or the
 * per-instance `whenReady` for individual ones (the latter doesn't yet
 * exist; today the host loader waits on the global ready then maps over
 * instances).
 */
export interface InstanceApi {
    /** Run validation, build payload, snapshot the combined image. */
    finalize(): Promise<FinalizeResult>;
    /** Inverse of buildAssetPayload: re-seed the store from a saved asset JSON. */
    restoreFromAssetJson(payload: Record<string, unknown>): void;
    /** Current price total + currency for read-only display in the host UI. */
    getPriceBreakdown(): PriceSnapshot;
    /**
     * Full read-only snapshot — selections, calculated outputs, rule
     * results, price. Pure function over store state; safe to call as
     * often as the host wants (e.g. inside a render loop).
     */
    getState(): ConfiguratorSnapshot;
    /** Just the selections sub-tree of {@link getState} — rule-filtered the
     *  same way. */
    getSelections(): SelectionsMap;
    /**
     * Read a single Characteristic's current value in the same shape
     * the expression engine sees it — short-cut over `getState()` when
     * the host only needs one field.
     *
     *   - single-pick: the picked Value's `value` string, or its id when
     *                  no `value` is set; `null` when nothing is picked
     *   - multi-pick:  array of value strings (ids if `value` is empty);
     *                  `[]` when nothing is picked
     *   - text/area:   the trimmed text input; `''` when empty
     *   - quantities:  `{ [valueId]: qty }` object; `{}` when none
     *   - calculated:  the evaluator output (number / string / `null`)
     *
     * A Characteristic a rule has removed reads as empty (`null` / `[]` /
     * `''` / `{}` per kind), exactly like one the customer never touched —
     * legacy nulls the value outright on blacklist (option/abstract.js:1158).
     *
     * Accepts the same {@link CharacteristicRef} the write methods do
     * — numeric id OR alt_title. Throws on unknown ref.
     */
    getValue(characteristic: CharacteristicRef): unknown;
    /**
     * Build the same alt_title-keyed asset payload `finalize()` would,
     * but WITHOUT running validation, snapshotting the combined image,
     * or surfacing per-Characteristic errors. Hosts that want to show
     * a "what would be submitted" preview before the user clicks the
     * action button can read this any time. Returns null if no
     * Characteristics are populated at all.
     *
     * Mirrors legacy `configurator.js:generateHeaderValue` without the
     * Base64 wrapping — see {@link getHeaderValue} for that.
     */
    getAssetPayload(): AssetPayload | null;
    /**
     * UTF-8-safe Base64 of the asset payload. This is the value the
     * legacy storefront posted in the `X-Configento` HTTP header and
     * the value Shopify-plugin embeds in `saveSnapshot.payload`. Hosts
     * that need to inject the snapshot into a form without going through
     * `finalize()` can read it directly.
     *
     * `opts.assets` adds the legacy header's top-level `assets` list — the
     * slot the Magento plugin reads the cart / checkout image from
     * (`imgData` entries). Pass what {@link storeCombinedImage} returned.
     * Skipped, with a warning, if a Characteristic's alt_title is `assets`.
     */
    getHeaderValue(opts?: HeaderValueOpts): string | null;

    /**
     * Render the combined product image of the current configuration and
     * store it through the admin's storefront asset API. Resolves to the
     * stored image's public `{ id, url }` for the host's cart / checkout, or
     * `null` when the Component has no combined image for this
     * configuration. Rejects with a ConfigentoCloudError when the upload
     * fails or the instance has no WebshopConfig — hosts treat that as
     * "no image", never as a reason to block the add-to-cart.
     */
    storeCombinedImage(): Promise<{ id: string; url: string } | null>;

    /**
     * POST the current configuration as a JSON asset to the Cloud and
     * return `{ id, url }` — `id` is the share token, `url` is the
     * direct static URL to the stored JSON. Hosts use the id to build
     * share-URLs for saved-configuration mails (typically `<product-url>?asset=<id>`)
     * — only the host plugin knows the product URL, so we don't try to
     * compose it here.
     *
     * Throws when no selections are populated yet, when the instance was
     * mounted without a `WebshopConfig` (e.g. admin-iframe preview), or
     * when the Cloud's `storeasset.php` endpoint refuses the POST.
     */
    storeAsset(opts?: StoreAssetOpts): Promise<{ id: string; url: string }>;

    /**
     * Render a single-string summary of the current visible selections —
     * `text` (default) joins entries with the chosen separator; `html`
     * emits a `<dl>` block suitable for embedding in a mail body. Pure
     * read; does not validate, does not snapshot. Empty payload → `''`.
     *
     * No new i18n keys are introduced: labels and value labels come
     * straight from the loaded Component (already localised). PDF
     * rendering lives in the server-side Save-Button-Action pipeline.
     */
    getSummary(opts?: BuildSummaryOpts): string;

    /**
     * SKU of the configured product, `A-B-C-D` from the article numbers of
     * the single choices — the same rule the Magento plugin uses for the cart
     * item. `''` when no part carries an article number. Also appended to
     * {@link getSummary} when the Component shows it in the summary.
     */
    getCombinedSku(): string;

    /**
     * Read-only description of every Characteristic, in Component order and
     * independent of rules and selections: internal name, group, CSS class
     * and the merchant-defined fields, plus the same for each Value.
     */
    getCharacteristics(): CharacteristicInfo[];

    /**
     * Programmatic single-pick: set the chosen Value for a single-pick
     * Characteristic (select / radiobuttons / single-pick listimage).
     * Pass `null` to clear. Accepts the Characteristic by numeric id OR
     * by its admin-authored `alt_title`; same for the Value (numeric id
     * OR the `value` string). Throws when the Characteristic doesn't
     * exist on this Component, when the Value doesn't exist on that
     * Characteristic, or when the type doesn't support single picks.
     */
    selectValue(characteristic: CharacteristicRef, value: ValueRef | null): void;
    /**
     * Programmatic multi-pick toggle: add the Value if missing, remove
     * it if present. Used for multiselect / checkbox / multi-pick
     * listimage. Refs accept id OR alt_title / value string. Throws on
     * unknown ref / wrong type.
     */
    toggleValue(characteristic: CharacteristicRef, value: ValueRef): void;
    /**
     * Set the free-entry text of a text or area Characteristic. Accepts
     * the Characteristic by id OR alt_title. Throws on unknown ref /
     * wrong type.
     */
    setText(characteristic: CharacteristicRef, value: string): void;
    /**
     * Set the per-Value quantity of a multiquantity Characteristic.
     * Both refs accept id OR alt_title / value. `quantity` is clamped
     * to ≥ 0 by the store. Throws on unknown ref / wrong type.
     */
    setQuantity(characteristic: CharacteristicRef, value: ValueRef, quantity: number): void;
    /**
     * Wipe a Characteristic's selection back to "none". Also clears the
     * userTouched flag so rule-driven predefines can repopulate it on
     * the next render. Ref accepts id OR alt_title. Throws on unknown ref.
     */
    resetSelection(characteristic: CharacteristicRef): void;

    /**
     * Typed multi-event subscription. Returns an unsubscribe function.
     *
     * Events:
     *   - `change`   — selection state mutated (any input type)
     *   - `price`    — total price actually shifted (deduped against no-op
     *                  re-renders)
     *   - `finalize` — `finalize()` resolved (success OR validation
     *                  failure — payload's `ok` tells you which)
     *   - `error`    — validation errors got surfaced into the store
     *                  (typically right after a failed `finalize()`)
     *   - `group`    — the visible group / wizard step changed
     *   - `action`   — an action button's request finished
     */
    on<E extends InstanceEventName>(
        event: E,
        cb: (payload: InstanceEventMap[E]) => void,
    ): () => void;

    /**
     * Subscribe to selection changes; returns an unsubscribe function.
     *
     * @deprecated Use `on('change', cb)` for the typed multi-event API.
     * Kept for back-compat with the original v3.0 surface; will be
     * removed in v4.0.
     */
    onChange(cb: () => void): () => void;
}

/**
 * Build the per-instance API for a configurator that has finished loading
 * its Component. Caller (main.tsx) wires this onto the registered
 * {@link ConfiguratorInstance} once `setState({kind:'loaded',…})` fires.
 *
 * Locale is fixed at instance-build time: snapshot summaries embed the
 * locale the user saw at that point in their session, so re-rendering
 * server-side later still picks up the right value labels.
 */
/**
 * Persistent (instance-lifetime) event-listener sets. Held on the
 * {@link MutableInstance} rather than inside each {@link createInstanceApi}
 * closure so late-attaching subscriptions (e.g. compat's
 * `inst.on('price', …)` registered at `Configento.ready`) survive
 * re-attachments — which happen every time externalValues finish
 * fetching. Without this, compat's cb would be orphaned in the previous
 * closure's Set and never receive events from the fresh closure with
 * the correct externalValues → the Magento priceBox ends up frozen at
 * the last emit from the stale closure (often €0 when the initial
 * externalValues were empty).
 */
export interface InstanceEventListeners {
    change:   Set<(p: ChangeEvent)   => void>;
    price:    Set<(p: PriceEvent)    => void>;
    finalize: Set<(p: FinalizeResult) => void>;
    error:    Set<(p: ErrorEvent)    => void>;
    group:    Set<(p: GroupChangeEvent) => void>;
    action:   Set<(p: ActionEvent) => void>;
}

export function createInstanceEventListeners(): InstanceEventListeners {
    return {
        change:   new Set(),
        price:    new Set(),
        finalize: new Set(),
        error:    new Set(),
        group:    new Set(),
        action:   new Set(),
    };
}

/**
 * Return shape of {@link createInstanceApi} — the InstanceApi surface
 * PLUS a `dispose()` handle that unsubscribes this closure's store
 * subscription. Callers MUST invoke dispose() on the previous bundle
 * before attaching a new one; skipping that leaks subscriptions across
 * externalValues re-fetches and eventually causes duplicate price emits
 * with drifting values (the €0 Magento priceBox bug).
 *
 * Intersection type (not a separate wrapping object) so existing tests
 * that treat the return as an InstanceApi keep working — e.g.
 * `createInstanceApi(...).getSummary(...)` reads fine, and callers that
 * care about lifetime destructure `{ dispose, ...api }`.
 */
/** One entry of the header's legacy `assets` list. */
export interface HeaderImageAsset {
    type: 'imgData';
    id: string;
    /** Absolute public URL — hosts use it as is, no cloud base prefix. */
    url: string;
    file: string;
}

export interface HeaderValueOpts {
    assets?: ReadonlyArray<HeaderImageAsset>;
}

export type CreateInstanceApiResult = InstanceApi & { dispose: () => void };

export function createInstanceApi(args: {
    component: Component;
    store: ConfigentoStore;
    locale: string | undefined;
    uiLocale: Locale;
    /** Host-resolved values for `webservice` + `productattribute` chars.
     *  Keyed by Characteristic id. Omit / pass empty Map when no host
     *  context is available — those chars then evaluate to null. */
    externalValues?: ExternalValues;
    /** Resolved WebshopConfig from the mount element. Required for
     *  `storeAsset()` (cloud upload) — admin-iframe / unit-test contexts
     *  may pass null, in which case `storeAsset` rejects with a clear
     *  message instead of silently no-oping. */
    webshopConfig?: WebshopConfig | null;
    /** Persistent event listeners, held on the MutableInstance so
     *  subscriptions survive re-attach. When omitted (tests / one-shot
     *  use), a fresh isolated set is created — behaviourally identical
     *  to the pre-refactor closure-local set. */
    eventListeners?: InstanceEventListeners;
    /** Host shop's display tax basis (percent) — the same value the React
     *  tree prices with. Every price this API hands the host (price events,
     *  `getPriceBreakdown`, `finalize().priceTotal`) must sit on that basis,
     *  or the host shows / carts a different amount than the configurator.
     *  Undefined → the Component's own basis (preview / unembedded). */
    storeTaxRate?: number | undefined;
}): CreateInstanceApiResult {
    const { component, store, locale, uiLocale, storeTaxRate } = args;
    const externalValues = args.externalValues ?? new Map<number, EvaluatedValue>();
    const webshopConfig  = args.webshopConfig ?? null;
    // The shop's display currency and rates: every price this API hands the
    // host is converted the same way the React tree converts it.
    const hostCurrency = {
        displayCurrency: webshopConfig?.currency,
        currencyRates: webshopConfig?.currencyRates,
    };

    // ── Event bus ────────────────────────────────────────────────────────
    // See {@link InstanceEventListeners} — sets are provided by the caller
    // (public-api's `registerInstance`) so re-attaches don't orphan
    // previously-registered callbacks.
    const listeners: InstanceEventListeners = args.eventListeners ?? createInstanceEventListeners();

    function emit<E extends InstanceEventName>(event: E, payload: InstanceEventMap[E]): void {
        for (const cb of listeners[event]) {
            try {
                (cb as (p: InstanceEventMap[E]) => void)(payload);
            } catch (err) {
                console.error(`[Configento] ${event} listener threw:`, err);
            }
        }
    }

    /**
     * Rules → effective selections → evaluator, in that order. Every method
     * below derives from this rather than re-assembling the sequence, so a
     * rule-removed Characteristic can't leak a price into one method while
     * another correctly drops it.
     */
    function derive(selections: SelectionsMap) {
        return deriveConfiguration({ component, selections, externalValues });
    }

    function breakdownOf({ rules, selections, evaluated }: DerivedConfiguration): PriceBreakdown {
        return calculatePrice(component, selections, evaluated, {
            storeTaxRate,
            ...hostCurrency,
            hiddenCharacteristics: rules.hiddenCharacteristics,
            blacklistedValues: rules.blacklistedValues,
        });
    }

    function priceOf(derived: DerivedConfiguration): PriceSnapshot {
        const price = breakdownOf(derived);
        return {
            total: price.total,
            currency: price.currency,
            priceIncludesTax: price.priceIncludesTax,
        };
    }

    // Hook into the store once; fan out to change + price listeners.
    let prevSelections = store.getState().selections;
    let prevPrice = priceOf(derive(prevSelections));
    let prevErrors = store.getState().validationErrors;
    const unsubStore = store.subscribe((next) => {
        // Identity check against the RAW store map — that's what actually
        // mutated. The payload below is the derived one.
        if (next.selections !== prevSelections) {
            prevSelections = next.selections;
            const derived = derive(next.selections);
            emit('change', { selections: derived.selections });

            const nextPrice = priceOf(derived);
            if (nextPrice.total !== prevPrice.total
                || nextPrice.currency !== prevPrice.currency
                || nextPrice.priceIncludesTax !== prevPrice.priceIncludesTax
            ) {
                emit('price', { ...nextPrice, delta: nextPrice.total - prevPrice.total });
                prevPrice = nextPrice;
            }
        }
        if (next.validationErrors !== prevErrors) {
            prevErrors = next.validationErrors;
            const flat: ValidationError[] = [];
            for (const errs of Object.values(next.validationErrors)) {
                for (const e of errs) flat.push(e);
            }
            if (flat.length > 0) {
                emit('error', { errors: flat });
            }
        }
    });

    // Re-emit price at attach-time so late-arriving consumers (compat
    // setPrice → Magento priceBox) reflect the current state. store.subscribe
    // only fires on future changes; without this, a re-attach triggered by
    // webservice values finishing after a deep-link restore would leave the
    // host priceBox frozen at whatever the previous closure last emitted
    // (often €0 with empty externalValues). Deferred to a microtask so
    // React's commit finishes before host code sees the event — matches
    // how the store subscribe callback is dispatched.
    queueMicrotask(() => {
        emit('price', { ...prevPrice, delta: 0 });
    });

    const api: InstanceApi = {
        finalize: async () => {
            const state = store.getState();
            const { rules, selections, evaluated } = derive(state.selections);

            const t: Parameters<typeof validateConfiguration>[3] = (key, params) =>
                translate(key, uiLocale, params);
            const errors = validateConfiguration(
                component,
                selections,
                rules,
                t,
                locale,
            );

            // Push errors into the store so each Characteristic surfaces
            // its own inline message even when the host triggered this
            // outside of the action button click path.
            state.setValidationErrors(errors);

            if (errors.length > 0) {
                const failResult: FinalizeResult = {
                    ok: false,
                    validationErrors: errors,
                    priceTotal: 0,
                    summary: '',
                };
                emit('finalize', failResult);
                return failResult;
            }

            const payload = buildAssetPayload({
                component,
                selections,
                evaluated,
                locale,
                uiLocale,
                hiddenCharacteristics: rules.hiddenCharacteristics,
            });
            const combinedImage = (await snapshotCombinedImage(component, selections)) ?? undefined;
            if (combinedImage) payload._combined_image = combinedImage;

            const price = calculatePrice(component, selections, evaluated, {
            storeTaxRate,
                ...hostCurrency,
                hiddenCharacteristics: rules.hiddenCharacteristics,
                blacklistedValues: rules.blacklistedValues,
            });

            const okResult: FinalizeResult = {
                ok: true,
                validationErrors: [],
                payload,
                combinedImage,
                priceTotal: price.total,
                summary: buildSummaryText(payload),
            };
            emit('finalize', okResult);
            return okResult;
        },

        restoreFromAssetJson: (payload) => {
            restoreFromAssetJson(component, payload, store);
        },

        getPriceBreakdown: () => {
            const state = store.getState();
            const { rules, selections, evaluated } = derive(state.selections);
            const price = calculatePrice(component, selections, evaluated, {
            storeTaxRate,
                ...hostCurrency,
                hiddenCharacteristics: rules.hiddenCharacteristics,
                blacklistedValues: rules.blacklistedValues,
            });
            return {
                total: price.total,
                currency: price.currency,
                priceIncludesTax: price.priceIncludesTax,
            };
        },

        getState: () => {
            const derived = derive(store.getState().selections);
            const evaluated: Record<number, EvaluatedValue> = {};
            for (const [id, v] of derived.evaluated.entries()) evaluated[id] = v;
            const breakdown = breakdownOf(derived);
            return {
                selections: derived.selections,
                evaluated,
                hiddenCharacteristics: [...derived.rules.hiddenCharacteristics],
                blacklistedValues: [...derived.rules.blacklistedValues],
                price: {
                    total: breakdown.total,
                    currency: breakdown.currency,
                    priceIncludesTax: breakdown.priceIncludesTax,
                },
                characteristicPrices: Object.fromEntries(amountsByName(breakdown)),
            };
        },

        getSelections: () => derive(store.getState().selections).selections,

        getValue: (charRef) => {
            const char = requireCharacteristic(component, charRef);
            // Read from the effective map, not the store: a rule-removed
            // Characteristic (and a stale pick on a blacklisted Value) has no
            // entry there, so it falls into the "nothing picked" branch below
            // and reads empty — same answer legacy gives after its
            // `setValue(null)`.
            const { selections, evaluated } = derive(store.getState().selections);
            // Calculated types: feed the evaluator and return whatever it
            // would have substituted into a dependent expression.
            if (char.type === 'expression' || char.type === 'combi'
                || char.type === 'matrixvalue' || char.type === 'productattribute'
            ) {
                return evaluated.get(char.id) ?? null;
            }
            return selectionValue(char, selections[char.id]);
        },

        getAssetPayload: () => {
            const state = store.getState();
            if (Object.keys(state.selections).length === 0) return null;
            const { rules, selections, evaluated } = derive(state.selections);
            return buildAssetPayload({
                component,
                selections,
                evaluated,
                locale,
                uiLocale,
                hiddenCharacteristics: rules.hiddenCharacteristics,
            });
        },

        getHeaderValue: (opts) => {
            const state = store.getState();
            if (Object.keys(state.selections).length === 0) return null;
            const { rules, selections, evaluated } = derive(state.selections);
            const payload = buildAssetPayload({
                component,
                selections,
                evaluated,
                locale,
                uiLocale,
                hiddenCharacteristics: rules.hiddenCharacteristics,
            });
            const assets = opts?.assets ?? [];
            if (assets.length === 0) return encodeBase64Utf8(JSON.stringify(payload));
            if ('assets' in payload) {
                console.warn('[Configento] a Characteristic is named "assets" — header image asset skipped');
                return encodeBase64Utf8(JSON.stringify(payload));
            }
            return encodeBase64Utf8(JSON.stringify({ ...payload, assets }));
        },

        storeCombinedImage: async () => {
            if (!webshopConfig) {
                throw new ConfigentoCloudError(
                    'CONFIG_MISSING',
                    'storeCombinedImage: this instance has no WebshopConfig — cloud upload not available',
                );
            }
            const { selections } = derive(store.getState().selections);
            const dataUrl = await snapshotCombinedImage(component, selections);
            if (!dataUrl) return null;
            const stored = await uploadCombinedImage(webshopConfig, dataUrl);
            return { id: stored.id, url: stored.url };
        },

        storeAsset: async (opts) => {
            const state = store.getState();
            if (Object.keys(state.selections).length === 0) {
                throw new ConfigentoCloudError(
                    'NO_SELECTIONS',
                    'storeAsset: no selections to store',
                );
            }
            if (!webshopConfig) {
                throw new ConfigentoCloudError(
                    'CONFIG_MISSING',
                    'storeAsset: this instance has no WebshopConfig — cloud upload not available',
                );
            }
            const { rules, selections, evaluated } = derive(state.selections);
            const payload = buildAssetPayload({
                component,
                selections,
                evaluated,
                locale,
                uiLocale,
                hiddenCharacteristics: rules.hiddenCharacteristics,
            });
            const stored = await storeAssetPayload(webshopConfig, payload, opts);
            return { id: stored.id, url: stored.url };
        },

        getSummary: (opts) => {
            const state = store.getState();
            if (Object.keys(state.selections).length === 0) return '';
            const derived = derive(state.selections);
            const { rules, selections, evaluated } = derived;
            const payload = buildAssetPayload({
                component,
                selections,
                evaluated,
                locale,
                uiLocale,
                hiddenCharacteristics: rules.hiddenCharacteristics,
            });
            const sku = component.showCombinedSku ? payload._combined_sku ?? '' : '';
            const prices = new Map<string, string>();
            if (opts?.includePrices === true) {
                const breakdown = breakdownOf(derived);
                for (const [name, amount] of amountsByName(breakdown)) {
                    if (amount !== 0) prices.set(name, formatCurrency(amount, breakdown.currency, uiLocale));
                }
            }
            return buildSummary(
                payload,
                opts,
                sku !== '' ? [{ label: translate('summary.sku', uiLocale), value: sku }] : [],
                prices,
            );
        },

        getCharacteristics: () => describeCharacteristics(component, locale),

        getCombinedSku: () => {
            const state = store.getState();
            const { rules, selections, evaluated } = derive(state.selections);
            return buildAssetPayload({
                component,
                selections,
                evaluated,
                locale,
                uiLocale,
                hiddenCharacteristics: rules.hiddenCharacteristics,
            })._combined_sku ?? '';
        },

        selectValue: (charRef, valueRef) => {
            const char = requireCharacteristic(component, charRef);
            requireSelectionType(char, 'single');
            const resolvedId = valueRef === null ? null : requireValueId(char, valueRef);
            store.getState().selectValue(char.id, resolvedId);
        },

        toggleValue: (charRef, valueRef) => {
            const char = requireCharacteristic(component, charRef);
            requireSelectionType(char, 'multi');
            const resolvedId = requireValueId(char, valueRef);
            store.getState().toggleValue(char.id, resolvedId);
        },

        setText: (charRef, value) => {
            const char = requireCharacteristic(component, charRef);
            requireSelectionType(char, 'text');
            store.getState().setText(char.id, value);
        },

        setQuantity: (charRef, valueRef, quantity) => {
            const char = requireCharacteristic(component, charRef);
            requireSelectionType(char, 'quantities');
            const resolvedId = requireValueId(char, valueRef);
            store.getState().setQuantity(char.id, resolvedId, quantity);
        },

        resetSelection: (charRef) => {
            const char = requireCharacteristic(component, charRef);
            store.getState().resetSelection(char.id);
        },

        on: <E extends InstanceEventName>(
            event: E,
            cb: (payload: InstanceEventMap[E]) => void,
        ) => {
            // Type-narrowed via Set<typeof cb>; the per-event Sets store the
            // same payload-shape so a cast at .add()/delete() is safe.
            const set = listeners[event] as unknown as Set<(p: InstanceEventMap[E]) => void>;
            set.add(cb);
            return () => set.delete(cb);
        },

        onChange: (cb) => {
            // Back-compat shim — same semantics as `on('change', …)` without
            // the typed payload. Marked @deprecated on the interface.
            const wrapper = (): void => cb();
            listeners.change.add(wrapper);
            return () => listeners.change.delete(wrapper);
        },
    };

    // Intersection: consumers can access every InstanceApi method AND
    // the `dispose` handle. Existing test code treats the return as an
    // InstanceApi and reads .finalize / .getSummary / etc. directly.
    // ConfiguratorRoot destructures `{ dispose, ...api }` to hand the
    // methods to attachInstanceApi and the dispose to the previous
    // closure's cleanup.
    const result: CreateInstanceApiResult = Object.assign(api, {
        // Cleanup for re-attach: unsubscribe this closure's store listener
        // so the next createInstanceApi call doesn't leave a leaked
        // subscription that continues to fire with stale externalValues
        // (the bug that caused the €0 Magento priceBox after deep-link
        // restore — the OLD closure kept emitting stale prices AFTER the
        // NEW closure was attached).
        dispose: () => unsubStore(),
    });
    return result;
}

/**
 * Throwing stub used until the Component has finished loading. Each
 * method throws so the host knows to wait on `Configento.ready` before
 * touching the API. We expose the same shape so TypeScript stays happy
 * and `instance.finalize` is never `undefined`.
 */
export function createPendingInstanceApi(): InstanceApi {
    const notReady = (): never => {
        throw new Error('Configento instance not ready — await Configento.ready first.');
    };
    return {
        finalize: () => Promise.reject(new Error('Configento instance not ready — await Configento.ready first.')),
        restoreFromAssetJson: notReady,
        getPriceBreakdown: notReady,
        getState: notReady,
        getSelections: notReady,
        getValue: notReady,
        getAssetPayload: notReady,
        getHeaderValue: notReady,
        storeAsset: () => Promise.reject(new Error('Configento instance not ready — await Configento.ready first.')),
        storeCombinedImage: () => Promise.reject(new Error('Configento instance not ready — await Configento.ready first.')),
        getSummary: notReady,
        getCombinedSku: notReady,
        getCharacteristics: notReady,
        selectValue: notReady,
        toggleValue: notReady,
        setText: notReady,
        setQuantity: notReady,
        resetSelection: notReady,
        on: notReady,
        onChange: notReady,
    };
}

/**
 * Look up a Characteristic by numeric id OR by its admin-authored
 * `alt_title`. Throws a clear error if neither matches. The "fail fast"
 * contract of every public write method depends on this — silent no-op
 * on unknown refs would just hide integration bugs.
 *
 * Alt-title matching is case-sensitive, mirroring the expression engine
 * (`expression.js` does the same — `$Lamelle_01` ≠ `$lamelle_01`).
 */
function requireCharacteristic(component: Component, ref: CharacteristicRef): Characteristic {
    for (const group of component.groups) {
        for (const char of group.characteristics) {
            if (typeof ref === 'number' ? char.id === ref : char.altTitle === ref) {
                return char;
            }
        }
    }
    const kind = typeof ref === 'number' ? '#' : 'alt_title=';
    throw new Error(
        `Configento: Characteristic ${kind}${ref} not found on Component ${component.id}.`,
    );
}

/**
 * Resolve a {@link ValueRef} to the Value's numeric id. Accepts the
 * numeric id directly OR the admin-authored `value` string (the same
 * one matrix lookups and expressions compare against). Throws when the
 * Characteristic doesn't actually carry the ref'd Value — most likely
 * a typo'd alt_title from the host code.
 *
 * Only used for refs that point INTO a Characteristic that has Values
 * (selection types). The single-`null` clear case is handled by the
 * caller before this is invoked.
 */
export function requireValueId(char: Characteristic, ref: ValueRef): number {
    if (!('values' in char)) {
        throw new Error(
            `Configento: Characteristic #${char.id} (type "${char.type}") doesn't carry Values — `
            + `cannot resolve value ref ${JSON.stringify(ref)}.`,
        );
    }
    for (const v of char.values) {
        if (typeof ref === 'number' ? v.id === ref : v.value === ref) {
            return v.id;
        }
    }
    const kind = typeof ref === 'number' ? '#' : 'value=';
    throw new Error(
        `Configento: Value ${kind}${ref} not found on Characteristic #${char.id} `
        + `(${char.altTitle ?? char.type}).`,
    );
}

/**
 * Map a Characteristic to the selection-kind its store entry uses.
 * Mirrors `state/selections.ts` semantics — the public write methods
 * use this to refuse e.g. `setText` on a single-pick Characteristic
 * rather than letting a silent store-write produce wrong state.
 */
export function selectionKindFor(char: Characteristic): 'single' | 'multi' | 'text' | 'quantities' | 'files' | 'captcha' | null {
    switch (char.type) {
        case 'select':
        case 'radiobuttons':
            return 'single';
        case 'multiselect':
        case 'checkbox':
            return 'multi';
        case 'listimage':
            return char.isMultiple ? 'multi' : 'single';
        case 'text':
        case 'area':
            return 'text';
        case 'multiquantity':
            return 'quantities';
        case 'fileupload':
            return 'files';
        case 'captcha':
            return 'captcha';
        // Calculated / display-only types accept no direct user input.
        case 'expression':
        case 'combi':
        case 'matrixvalue':
        case 'productattribute':
        case 'webservice':
        case 'package':
        case 'chart':
        case 'savebutton':
        case 'static':
        case 'unknown':
            return null;
    }
}

export function requireSelectionType(
    char: Characteristic,
    expected: 'single' | 'multi' | 'text' | 'quantities' | 'files',
): void {
    const actual = selectionKindFor(char);
    if (actual !== expected) {
        const actualLabel = actual ?? 'no-input';
        throw new Error(
            `Configento: Characteristic #${char.id} is type "${char.type}" `
            + `(selection kind "${actualLabel}"), cannot accept a "${expected}" write.`,
        );
    }
}

/**
 * A selection in the shape `getValue()` returns: the Value's `value` field
 * (or id), a list for multi-picks, text, quantities, files — and an empty
 * default per kind when nothing is picked. Calculated types are handled by
 * the caller.
 */
export function selectionValue(char: Characteristic, sel: CharacteristicSelection | undefined): unknown {
    if (!sel) {
        // Empty defaults per kind so the host doesn't have to
        // branch on undefined first.
        switch (selectionKindFor(char)) {
            case 'single':     return null;
            case 'multi':      return [];
            case 'text':       return '';
            case 'quantities': return {};
            case 'files':      return [];
            default:           return null;
        }
    }
    switch (sel.kind) {
        case 'single':
            if (sel.valueId === null) return null;
            if ('values' in char) {
                const picked = char.values.find((v) => v.id === sel.valueId);
                return picked?.value ?? picked?.id ?? null;
            }
            return sel.valueId;
        case 'multi':
            if (!('values' in char)) return [...sel.valueIds];
            return sel.valueIds.map((id) => {
                const v = char.values.find((vv) => vv.id === id);
                return v?.value ?? v?.id ?? id;
            });
        case 'text':
            return sel.value;
        case 'quantities':
            return { ...sel.quantities };
        case 'files':
            return sel.files.map((f) => ({ ...f }));
    }
}

/**
 * UTF-8-safe Base64 encoder. `btoa()` alone throws on any code point > 0xFF,
 * which configurator content frequently has (€, ü, …). Encode to UTF-8
 * first, then to Base64. Pure function so the same helper works in tests
 * (jsdom) and browser builds.
 */
function encodeBase64Utf8(s: string): string {
    if (typeof TextEncoder !== 'undefined' && typeof btoa !== 'undefined') {
        const bytes = new TextEncoder().encode(s);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i] ?? 0);
        return btoa(binary);
    }
    // Node (test env without browser globals) fallback.
    return Buffer.from(s, 'utf-8').toString('base64');
}

/** Price lines summed per Characteristic, keyed by internal name. */
function amountsByName(breakdown: PriceBreakdown): Map<string, number> {
    const out = new Map<string, number>();
    for (const line of breakdown.lines) {
        const name = line.characteristic.altTitle;
        if (!name) continue;
        out.set(name, (out.get(name) ?? 0) + line.amount);
    }
    return out;
}

function describeCharacteristics(component: Component, locale: string | undefined): CharacteristicInfo[] {
    const defs = component.customFieldDefinitions ?? { characteristics: [], values: [] };
    const describeValue = (v: Value): ValueInfo => ({
        id: v.id,
        key: v.value ?? null,
        label: pickLocale(v.title, locale) ?? '',
        sku: v.sku ?? null,
        customFields: resolveCustomFields(v.customFields ?? {}, defs.values),
    });
    const flatten = (vs: readonly Value[]): Value[] => vs.flatMap((v) => [v, ...flatten(v.children)]);
    return component.groups.flatMap((g) => g.characteristics.map((c): CharacteristicInfo => ({
        id: c.id,
        key: c.altTitle || null,
        groupId: g.id,
        groupKey: g.altTitle ?? null,
        title: pickLocale(c.title, locale) ?? '',
        type: c.type,
        visibility: c.visibility,
        cssClass: c.cssClass ?? '',
        customFields: resolveCustomFields(c.customFields ?? {}, defs.characteristics),
        values: 'values' in c && Array.isArray(c.values) ? flatten(c.values).map(describeValue) : [],
    })));
}
